import React, { useEffect, useState } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { tagStyle } from '../theme/statusTokens';
import {
  getWatchHue,
  getWatchLabel,
  hasWatchState,
  isTerminalWatchState,
  normalizeWatchState,
} from '../theme/watchTokens';
import { formatCountdown, watchRemainingMs } from '../lib/watch';

export interface WatchPillProps {
  record: DeploymentRecord;
  size?: 'small' | 'medium';
}

const humanizeReason = (reason?: string): string =>
  (reason || '').replace(/_/g, ' ');

/**
 * Auto-rollback watch-state pill (DEVOPS-1629). Renders all 7 states; the
 * `watching` state shows a live countdown to the end of the watch window, and
 * terminal states reveal their `watch_end_reason` on hover.
 */
const WatchPill: React.FC<WatchPillProps> = ({ record, size = 'small' }) => {
  const theme = useTheme();
  const state = normalizeWatchState(record.watch_state);

  // Tick once a second while watching so the countdown stays live. The interval
  // is only armed for the watching state; other states never re-render here.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (state !== 'watching') return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [state]);

  if (!hasWatchState(record.watch_state)) return null;

  const style = tagStyle(getWatchHue(record.watch_state), theme.palette.mode);

  let label = getWatchLabel(record.watch_state);
  if (state === 'watching') {
    const remaining = watchRemainingMs(record, now);
    if (remaining !== null) label = `Monitoring — ${formatCountdown(remaining)}`;
  }

  const tooltip =
    isTerminalWatchState(record.watch_state) && record.watch_end_reason
      ? `${getWatchLabel(record.watch_state)}: ${humanizeReason(record.watch_end_reason)}`
      : '';

  const chip = (
    <Chip
      label={label}
      size={size}
      sx={{
        backgroundColor: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        fontWeight: 600,
        fontSize: '0.72rem',
        height: 22,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

export default WatchPill;
